import { useEffect, useMemo, useState } from 'react';
import { admin, typography } from '../../../../styles/tokens';
import DedupePreviewTable from '../shared/DedupePreviewTable';
import { defaultDecisionFor } from '../shared/dedupe';
import { useImportClients } from '../shared/useImportClients';
import type {
  DedupeDecision,
  DuplicateMatch,
  NormalizedRow,
} from '../shared/types';

interface PreviewStepProps {
  normalized: NormalizedRow[];
  decisions: Record<string, DedupeDecision>;
  onDecisionsChange: (next: Record<string, DedupeDecision>) => void;
  onImport: () => void;
}

export default function PreviewStep({
  normalized,
  decisions,
  onDecisionsChange,
  onImport,
}: PreviewStepProps) {
  const { findDuplicates } = useImportClients();
  const [matches, setMatches] = useState<DuplicateMatch[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const found = await findDuplicates(normalized);
        if (cancelled) return;
        setMatches(found);
        // Seed defaults for any row we haven't decided on yet.
        const seeded: Record<string, DedupeDecision> = { ...decisions };
        let dirty = false;
        for (const m of found) {
          if (!seeded[m.incoming.rowKey]) {
            seeded[m.incoming.rowKey] = defaultDecisionFor(m.strength);
            dirty = true;
          }
        }
        if (dirty) onDecisionsChange(seeded);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setError(`Couldn't check for duplicates: ${msg}`);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalized]);

  const { cleanRows, actualMatches } = useMemo(() => {
    if (!matches) return { cleanRows: [], actualMatches: [] as DuplicateMatch[] };
    const matchedKeys = new Set(matches.map(m => m.incoming.rowKey));
    const clean = normalized.filter(r => !matchedKeys.has(r.rowKey));
    return { cleanRows: clean, actualMatches: matches };
  }, [matches, normalized]);

  if (error) {
    return (
      <div style={{ color: admin.body, fontSize: typography.size.body }}>
        {error}
      </div>
    );
  }

  if (matches === null) {
    return (
      <div style={{ color: admin.caption, fontSize: typography.size.caption }}>
        Checking for duplicates…
      </div>
    );
  }

  return (
    <DedupePreviewTable
      matches={actualMatches}
      cleanRows={cleanRows}
      decisions={decisions}
      onDecisionsChange={onDecisionsChange}
      onImport={onImport}
      importing={false}
    />
  );
}
